import { useCallback } from 'react';

import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  useUndo,
  useWaitForUndoStack,
} from '@client/hooks/use-undo-redo.hooks';
import {
  getProject,
  useUpdateProject,
} from '@client/store/project/project.hook';
import { getApplyProjectChangesSnackbarCallbacks } from '@client/store/project/apply-project-changes-snackbar.helpers';
import type {
  ApplyProjectChangesOptions,
  ApplyProjectChangesResult,
} from '@client/store/project/use-apply-project-changes.hook';
import { type BaselineRecipeOverwriteTarget } from '@shared/helpers/actions/baseline-recipe-overwrite.helpers';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import {
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { overwriteRecipe } from '@shared/helpers/recipe.helpers';

/**
 * Persists {@link overwriteRecipe} on baseline elements (recipe library save / reset).
 * Does not append proposal actions; enrich scope uses synthetic {@link ProjectChangeType.ElementRecipeApply} rows.
 */
export const useApplyBaselineRecipeOverwrite = (
  options: ApplyProjectChangesOptions = {},
): ((
  targets: readonly BaselineRecipeOverwriteTarget[],
  optionsOverrides?: ApplyProjectChangesOptions,
) => Promise<ApplyProjectChangesResult>) => {
  const updateProject = useUpdateProject();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const undo = useUndo();
  const waitForUndoStack = useWaitForUndoStack();
  const memoOptions = useObjectMemo(options);

  return useCallback(
    async (targets, optionsOverrides = {}) => {
      const preProject = getProject();
      if (targets.length === 0) return { project: preProject };

      const merged = { ...memoOptions, ...optionsOverrides };
      const {
        snackbar: snackbarOptions,
        appliedChanges: _appliedChanges,
        enrichSelectedProposalId: _enrichSelectedProposalId,
        enrichScope: _enrichScope,
        skipEnrichment: _skipEnrichment,
        serverOnly: _serverOnly,
        ...updateOptions
      } = merged;

      const changes = targets.map(({ elementId, recipe }) => {
        const recipeId = recipe.id;
        if (!recipeId) {
          throw new Error(
            'Recipe id is required to update element from recipe',
          );
        }
        // 'baseline' + explicit fallbackName reproduces the direct-apply result
        // (clean-slate overwriteRecipe) both on first save and on 422 replay.
        const element = getElementById(preProject, elementId, false);
        const fallbackName =
          element && isElement(element)
            ? overwriteRecipe(element, recipe).fallbackName
            : undefined;
        return {
          type: ProjectChangeType.ElementRecipeApply as const,
          elementId,
          recipeId,
          recipe,
          overwriteMode: 'baseline' as const,
          ...(fallbackName !== undefined ? { fallbackName } : {}),
        };
      });

      const snackbarCallbacks = snackbarOptions
        ? getApplyProjectChangesSnackbarCallbacks({
            snackbar: snackbarOptions,
            enqueueSnackbar,
            closeSnackbar,
            undo,
            waitForUndoStack,
          })
        : {};

      const result = await updateProject({
        kind: 'changes',
        baseline: preProject,
        changes,
        ...updateOptions,
        ...snackbarCallbacks,
      });

      return { project: result };
    },
    [
      closeSnackbar,
      enqueueSnackbar,
      memoOptions,
      undo,
      updateProject,
      waitForUndoStack,
    ],
  );
};
